/**
 * useSessionsSync
 *
 * Architecture: Component → Hook → API/Realtime → Store (state only)
 *
 * This hook owns the full lifecycle of keeping sessions in sync
 * with the Django backend when running inside the authenticated dashboard:
 *   1. Triggers the initial REST load via `loadSessions()`.
 *   2. Opens a WebSocket subscription for sessions.
 *   3. Pushes create / update / delete events directly into the store.
 *   4. Cleans up the subscription on unmount (handles the
 *      unmount-before-resolve race with a `mounted` flag).
 *
 * The Zustand store is intentionally kept as pure state — all
 * orchestration (API call + subscription lifecycle) lives here.
 */
import { useEffect } from 'react'

import { subscribeSessions } from '@/core/api/realtime'

import { useClassSessionStore } from '../stores'
import type { ClassSession } from '../stores/types'

export function useSessionsSync() {
  const loadSessions = useClassSessionStore((s) => s.loadSessions)

  useEffect(() => {
    let mounted = true
    let unsub: (() => void) | null = null
    let retryTimer: ReturnType<typeof setTimeout> | null = null

    // Refetch attendees for the affected session; retry once after ~1.5s because
    // the broadcast can arrive before the create commit is visible to readers.
    const refetchAttendees = (sessionId: string, isRetry = false) => {
      useClassSessionStore
        .getState()
        .loadSessionAttendees(sessionId)
        .catch((err: unknown) => {
          if (isRetry) {
            console.error('[WS] Attendee refetch retry failed:', err)
            return
          }
          retryTimer = setTimeout(() => {
            if (mounted) {
              refetchAttendees(sessionId, true)
            }
          }, 1500)
        })
    }

    // Step 1 — realtime subscription via WebSocket; apply events directly to the store.
    // Established unconditionally so a failed initial REST load doesn't disable realtime.
    const fn = subscribeSessions('*', (action, record) => {
        console.warn('[WS] Received event:', action, record)

        // Detect attendee events vs session events by checking for attendee-specific fields
        const hasAttendeeFields = record.student_email !== undefined || record.studentId !== undefined || record.student_id !== undefined || record.quiz_score !== undefined || record.quiz_answers !== undefined
        const hasSessionFields = record.qr_token !== undefined || record.qrToken !== undefined || record.course_id !== undefined || record.courseId !== undefined || record.location !== undefined
        const isAttendeeEvent = hasAttendeeFields && !hasSessionFields

        if (isAttendeeEvent) {
          // Attendee realtime event — reload attendees for the affected session
          const sessionId = (record.session_id as string) ?? (record.sessionId as string)
          console.warn('[WS] Attendee event for session:', sessionId)
          if (sessionId) {
            refetchAttendees(sessionId)
          }
          return
        }

        // WebSocket payloads arrive in snake_case from the backend;
        // fall back to camelCase in case the data was already converted.
        const incoming: ClassSession = {
          id: record.id as string,
          qrToken: (record.qr_token as string) ?? (record.qrToken as string) ?? '',
          courseId: (record.course_id as ClassSession['courseId']) ?? (record.courseId as ClassSession['courseId']),
          location:
            (record.location_name as string) ??
            (record.locationName as string) ??
            (record.location as string) ??
            '',
          locationName:
            (record.location_name as string) ?? (record.locationName as string) ?? undefined,
          date: (record.date as string) ?? '',
          startTime: (record.start_time as string) ?? (record.startTime as string) ?? undefined,
          endTime: (record.end_time as string) ?? (record.endTime as string) ?? undefined,
          durationMinutes: (record.duration_minutes as number) ?? (record.durationMinutes as number) ?? 0,
          status: (record.status as ClassSession['status']) ?? 'draft',
          instructorName: (record.instructor_name as string) ?? (record.instructorName as string) ?? '',
          maxAttendees:
            (record.max_attendees as number | undefined) ??
            (record.maxAttendees as number | undefined) ??
            undefined,
          spotsAvailable:
            (record.spots_available as number | undefined) ??
            (record.spotsAvailable as number | undefined) ??
            undefined,
          language: (record.language as string) ?? 'any',
          startedAt: (record.started_at ?? record.startedAt ?? null) as string | null,
          closedAt: (record.closed_at ?? record.closedAt ?? null) as string | null,
        }

        // Step 4 — push state into the Zustand store (store = pure state)
        if (action === 'create') {
          // Guard against duplicates: createSession() already adds the session
          // optimistically. If the realtime event arrives after that, update the
          // record in-place (the backend response is the source of truth) instead of
          // prepending a second copy.
          useClassSessionStore.setState((s) => {
            const exists = s.sessions.some((sess) => sess.id === (record.id as string))
            const updated = exists
              ? s.sessions.map((sess) => (sess.id === (record.id as string) ? incoming : sess))
              : [incoming, ...s.sessions]
            return {
              sessions: updated.sort((a, b) =>
                a.date < b.date ? 1 : a.date > b.date ? -1 : 0
              ),
            }
          })
        } else if (action === 'update') {
          useClassSessionStore.setState((s) => ({
            sessions: s.sessions.map((sess) =>
              sess.id === (record.id as string) ? { ...sess, ...incoming } : sess
            ),
          }))
        } else if (action === 'delete') {
          useClassSessionStore.setState((s) => ({
            sessions: s.sessions.filter((sess) => sess.id !== (record.id as string)),
          }))
        }
      })
    unsub = fn

    // Step 2 — initial REST fetch; loadSessions sets sessionsLoaded + error in store.
    // Runs independently of the subscription above.
    loadSessions().catch(() => {
      // Error is already recorded in the store; realtime stays subscribed.
    })

    return () => {
      mounted = false
      if (retryTimer) {
        clearTimeout(retryTimer)
      }
      unsub?.()
    }
  }, [loadSessions])
}
